import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import ClubMap from '../components/ClubMap';
import { getAllClubs } from '../services/api';
import { MapPin, AlertCircle, Navigation, Users, Building2, TrendingUp } from 'lucide-react';
import { DotLoader } from 'react-spinners';
import Footer from '../components/Footer';

function MapView() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    fetchClubs();
    getUserLocation();
  }, []);

  const fetchClubs = async () => {
    try {
      const data = await getAllClubs();
      setClubs(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
          setLocationError('Impossible d\'obtenir votre position. La carte sera centrée par défaut.');
          setLocationLoading(false);
        }
      );
    } else {
      setLocationError('La géolocalisation n\'est pas supportée par votre navigateur.');
      setLocationLoading(false);
    }
  };

  const requestLocation = () => {
    setLocationError(null);
    getUserLocation();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  const totalMembers = clubs.reduce((sum, club) => sum + (club.membres?.length || 0), 0);
  const uniqueCities = new Set(clubs.map(club => club.ville)).size;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <MapPin className="text-indigo-500" size={28} />
            Carte des Run Clubs
          </h1>
          <p className="text-sm sm:text-base text-gray-400">
            Découvrez tous les clubs disponibles autour de vous
          </p>
        </div>

        {/* Alerte de localisation */}
        {locationError && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-yellow-900/20 border border-yellow-500 rounded-xl">
            <div className="flex items-start">
              <AlertCircle className="text-yellow-500 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-yellow-400 text-sm sm:text-base">{locationError}</p>
                <button
                  onClick={requestLocation}
                  className="mt-2 text-sm font-semibold text-yellow-400 hover:text-yellow-300 underline"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading localisation */}
        {locationLoading && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-indigo-900/20 border border-indigo-500 rounded-xl">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 mr-3"></div>
              <p className="text-indigo-400 text-sm sm:text-base font-medium">
                Localisation en cours...
              </p>
            </div>
          </div>
        )}

        {/* Statistiques */}
        <div className="mb-4 sm:mb-6 grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-6 text-center hover:border-indigo-500 transition-all">
            <div className="flex justify-center mb-2">
              <div className="bg-indigo-900/30 border border-indigo-500 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center">
                <MapPin size={20} className="text-indigo-500 sm:w-6 sm:h-6" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-indigo-500">{clubs.length}</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1 font-medium">Clubs</p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-6 text-center hover:border-indigo-500 transition-all">
            <div className="flex justify-center mb-2">
              <div className="bg-purple-900/30 border border-purple-500 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center">
                <Users size={20} className="text-purple-500 sm:w-6 sm:h-6" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-purple-500">{totalMembers}</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1 font-medium">Membres</p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-6 text-center hover:border-indigo-500 transition-all">
            <div className="flex justify-center mb-2">
              <div className="bg-green-900/30 border border-green-500 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center">
                <Building2 size={20} className="text-green-500 sm:w-6 sm:h-6" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-green-500">{uniqueCities}</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1 font-medium">Villes</p>
          </div>
        </div>

        {/* Carte */}
        <ClubMap clubs={clubs} userLocation={userLocation} />

        {/* Bouton d'action flottant pour explorer */}
        <button
          onClick={() => navigate('/')}
          className="md:hidden fixed bottom-6 right-6 bg-indigo-500 hover:bg-indigo-600 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/20 transition-all transform hover:scale-110 active:scale-95 z-40 flex items-center justify-center"
          aria-label="Explorer les clubs"
        >
          <TrendingUp size={24} />
        </button>
      </main>
      <Footer />
    </div>
  );
}

export default MapView;